import type { ComponentType } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, Eye } from 'lucide-react';
import { Badge } from '@/app/components/ui/badge';
import type { ClauseStatusDetail, ClauseStatusValue } from '@/lib/types';

type StatusIcon = ComponentType<{ className?: string }>;

export const STATUS_CONFIG: Record<
  ClauseStatusValue,
  { label: string; bg: string; text: string; icon: StatusIcon }
> = {
  compliant: {
    label: 'Compliant',
    bg: 'bg-green-100',
    text: 'text-green-700',
    icon: CheckCircle2,
  },
  partially_compliant: {
    label: 'Partial',
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    icon: AlertTriangle,
  },
  non_compliant: {
    label: 'Non-compliant',
    bg: 'bg-red-100',
    text: 'text-red-700',
    icon: XCircle,
  },
  not_applicable: {
    label: 'N/A',
    bg: 'bg-gray-100',
    text: 'text-gray-500',
    icon: HelpCircle,
  },
  not_assessed: {
    label: 'Not assessed',
    bg: 'bg-gray-100',
    text: 'text-gray-500',
    icon: HelpCircle,
  },
};

export const MANUAL_REVIEW_CONFIG = {
  label: 'Manual review required',
  bg: 'bg-violet-100',
  text: 'text-violet-700',
};

export function needsManualReview(clause: ClauseStatusDetail): boolean {
  return clause.status === 'not_assessed' && !clause.auto_collectible;
}

export function StatusPill({
  status,
  manualReview,
}: {
  status: ClauseStatusValue;
  manualReview?: boolean;
}) {
  const cfg =
    manualReview && status === 'not_assessed'
      ? MANUAL_REVIEW_CONFIG
      : STATUS_CONFIG[status];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.text}`}
    >
      {manualReview && status === 'not_assessed' && (
        <Eye className="mr-1 h-3 w-3" />
      )}
      {cfg.label}
    </span>
  );
}

export function ScopeTagsBadge({ tags }: { tags: string[] }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-700"
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

export function StatusBadge({ status }: { status: ClauseStatusValue }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.not_assessed;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.bg} ${cfg.text} shrink-0`}>
      <Icon className="mr-1 h-3 w-3" />
      {cfg.label}
    </Badge>
  );
}
